import React, { useContext } from "react";
import { motion } from "framer-motion";
import StyleContext from "../../contexts/StyleContext";
import { techStack, illustration } from "../../portfolio";
import DisplayLottie from "../../components/displayLottie/DisplayLottie";
import codingPerson from "../../assets/lottie/codingPerson";

export default function SkillProgressModern() {
  const { isDark } = useContext(StyleContext);

  if (!techStack.viewSkillBars) {
    return null;
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.5 },
    },
  };

  // Function to get color based on percentage
  const getColorFromPercentage = (percentage) => {
    const value = parseInt(percentage);
    if (value >= 80) return { from: "from-green-500", to: "to-emerald-400", text: "text-green-500" };
    if (value >= 60) return { from: "from-blue-500", to: "to-cyan-400", text: "text-blue-500" };
    if (value >= 40) return { from: "from-yellow-500", to: "to-orange-400", text: "text-yellow-500" };
    return { from: "from-red-500", to: "to-pink-400", text: "text-red-500" };
  };

  return (
    <div className="relative py-20 md:py-32 overflow-hidden">
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-cyan-50 to-blue-50 dark:from-gray-900 dark:via-cyan-900/20 dark:to-blue-900/20" />

      {/* Floating Orb */}
      <motion.div
        className="absolute left-0 top-1/3 w-96 h-96 bg-gradient-to-r from-cyan-400 to-blue-400 rounded-full opacity-20 blur-3xl"
        animate={{
          scale: [1, 1.2, 1],
          x: [0, 50, 0],
          y: [0, -30, 0],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      <div className="relative z-10 container mx-auto px-4">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <motion.div
            className="inline-block mb-4"
            whileHover={{ scale: 1.05 }}
          >
            <span className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-full text-sm font-semibold shadow-lg">
              📊 Skills Assessment
            </span>
          </motion.div>
          <h2 className={`text-4xl md:text-5xl lg:text-6xl font-bold mb-6 ${
            isDark ? "text-white" : "text-gray-900"
          }`}>
            Proficiency
          </h2>
          <p className={`text-lg md:text-xl max-w-2xl mx-auto ${
            isDark ? "text-gray-400" : "text-gray-600"
          }`}>
            My expertise across different technology stacks and domains
          </p>
        </motion.div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Skills Grid */}
            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              className="grid grid-cols-2 sm:grid-cols-3 gap-6"
            >
              {techStack.experience.map((exp, index) => {
                const percentage = parseInt(exp.progressPercentage);
                const colors = getColorFromPercentage(exp.progressPercentage);

                return (
                  <motion.div
                    key={index}
                    variants={itemVariants}
                    whileHover={{ scale: 1.05 }}
                    className="glass backdrop-blur-xl rounded-2xl p-6 bg-white/60 dark:bg-gray-800/60 border border-white/20 dark:border-gray-700/20 shadow-xl hover:shadow-2xl transition-all duration-300"
                  >
                    {/* Circular Progress */}
                    <div className="relative w-32 h-32 mx-auto mb-4">
                      {/* Background Circle */}
                      <svg className="transform -rotate-90 w-32 h-32">
                        <circle
                          cx="64"
                          cy="64"
                          r="56"
                          stroke="currentColor"
                          strokeWidth="8"
                          fill="transparent"
                          className={isDark ? "text-gray-700" : "text-gray-200"}
                        />
                        {/* Progress Circle */}
                        <motion.circle
                          cx="64"
                          cy="64"
                          r="56"
                          stroke="url(#gradient-{index})"
                          strokeWidth="8"
                          fill="transparent"
                          strokeLinecap="round"
                          strokeDasharray={`${2 * Math.PI * 56}`}
                          initial={{ strokeDashoffset: 2 * Math.PI * 56 }}
                          whileInView={{
                            strokeDashoffset: 2 * Math.PI * 56 * (1 - percentage / 100)
                          }}
                          viewport={{ once: true }}
                          transition={{ duration: 1.5, ease: "easeOut" }}
                        />
                        {/* Gradient Definition */}
                        <defs>
                          <linearGradient id={`gradient-${index}`} x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" className={colors.from.replace('from-', 'text-')} stopColor="currentColor" />
                            <stop offset="100%" className={colors.to.replace('to-', 'text-')} stopColor="currentColor" />
                          </linearGradient>
                        </defs>
                      </svg>

                      {/* Percentage Text */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className={`text-2xl font-bold ${colors.text} ${isDark ? 'brightness-125' : ''}`}>
                          {percentage}%
                        </span>
                      </div>
                    </div>

                    {/* Stack Name */}
                    <h3 className={`text-center text-sm font-semibold ${
                      isDark ? "text-white" : "text-gray-900"
                    }`}>
                      {exp.Stack}
                    </h3>
                  </motion.div>
                );
              })}
            </motion.div>

            {/* Illustration */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="hidden lg:block"
            >
              <div className="glass backdrop-blur-xl rounded-3xl p-8 bg-white/40 dark:bg-gray-800/40 border border-white/20 dark:border-gray-700/20 shadow-2xl">
                {illustration.animated ? (
                  <DisplayLottie animationData={codingPerson} />
                ) : (
                  <img
                    alt="Developer Working"
                    src={require("../../assets/images/developerActivity.svg")}
                    className="w-full h-auto"
                  />
                )}
              </div>
            </motion.div>
          </div>
        </div>

        {/* Bottom Decorative Element */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="mt-20 flex justify-center"
        >
          <div className="flex items-center gap-2">
            <div className="h-px w-20 bg-gradient-to-r from-transparent via-cyan-500 to-transparent" />
            <div className="w-2 h-2 rounded-full bg-cyan-500" />
            <div className="h-px w-20 bg-gradient-to-r from-transparent via-blue-500 to-transparent" />
          </div>
        </motion.div>
      </div>
    </div>
  );
}
